/**
 * Enhanced Podcast Management
 * 
 * Comprehensive podcast management with beautiful cards, stats, and management tools.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

function PodcastManagement() {
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const { currentUser, userProfile, isCreator } = useAuth();
  
  // Fetch podcasts on component mount
  useEffect(() => {
    async function fetchPodcasts() {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Query podcasts where creatorId equals current user's uid
        const podcastsQuery = query(
          collection(db, "podcasts"),
          where("creatorId", "==", currentUser.uid)
        );
        
        const querySnapshot = await getDocs(podcastsQuery);
        
        const podcastsList = [];
        querySnapshot.forEach((doc) => {
          podcastsList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        console.log(`Found ${podcastsList.length} podcasts for user ${currentUser.uid}`);
        setPodcasts(podcastsList);
        
      } catch (err) {
        console.error('Error fetching podcasts:', err);
        setError('Failed to load podcasts: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    
    fetchPodcasts();
  }, [currentUser]);
  
  function formatLastUpdated(timestamp) {
    if (!timestamp) return 'N/A';
    
    try {
      // Handle Firestore Timestamp
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return new Date(timestamp.toDate()).toLocaleDateString();
      }
      
      // Handle regular Date or timestamp
      return new Date(timestamp).toLocaleDateString();
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'N/A';
    }
  }
  
  function getEpisodeCount(podcast) {
    // Handle different episode data structures
    if (podcast.episodes) {
      if (Array.isArray(podcast.episodes)) {
        return podcast.episodes.length;
      } else if (typeof podcast.episodes === 'object') {
        return Object.keys(podcast.episodes).length;
      }
    }
    return 0;
  }

  // Auth checks
  if (!currentUser) {
    return (
      <div style={{ 
        padding: '60px 40px',
        textAlign: 'center',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔐</div>
        <h2 style={{ marginBottom: '15px' }}>Authentication Required</h2>
        <p style={{ color: '#666', marginBottom: '30px' }}>
          Please log in to access podcast management.
        </p>
        <Link to="/login" style={{ 
          display: 'inline-block',
          padding: '15px 30px',
          background: '#4a69dd',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '8px',
          fontWeight: '600'
        }}>
          Log In
        </Link>
      </div>
    );
  }

  if (!isCreator) {
    return (
      <div style={{ 
        padding: '60px 40px',
        textAlign: 'center',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>👨‍💼</div>
        <h2 style={{ marginBottom: '15px' }}>Creator Access Required</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          You need to be registered as a creator to manage podcasts.
        </p>
        <p style={{ color: '#999', fontSize: '14px', marginBottom: '30px' }}>
          Current user: {currentUser?.email}
        </p>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/creator-dashboard" style={{ 
            display: 'inline-block',
            padding: '15px 30px',
            background: '#4a69dd',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600'
          }}>
            Back to Dashboard
          </Link>
          <Link to="/for-creators" style={{ 
            display: 'inline-block',
            padding: '15px 30px',
            background: 'white',
            color: '#333',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            border: '2px solid #ddd'
          }}>
            Become a Creator
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '20px',
        padding: '40px',
        color: 'white',
        marginBottom: '40px',
        boxShadow: '0 10px 40px rgba(102, 126, 234, 0.2)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '30px',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <h1 style={{ margin: '0 0 10px 0', fontSize: '32px', fontWeight: '700' }}>
              Podcast Management
            </h1>
            <p style={{ margin: 0, opacity: '0.9', fontSize: '18px' }}>
              Manage all your podcasts, track performance, and grow your audience
            </p>
          </div>
          <Link to="/creator-dashboard" style={{ 
            padding: '12px 24px',
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            border: '1px solid rgba(255,255,255,0.3)',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
          onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
          >
            ← Back to Dashboard
          </Link>
        </div>

        {/* Summary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '12px',
            padding: '25px',
            textAlign: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '5px' }}>
              {podcasts.length}
            </div>
            <div style={{ fontSize: '14px', opacity: '0.8' }}>Total Podcasts</div>
          </div>
          
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '12px',
            padding: '25px',
            textAlign: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '5px' }}>
              {podcasts.reduce((total, podcast) => total + getEpisodeCount(podcast), 0)}
            </div>
            <div style={{ fontSize: '14px', opacity: '0.8' }}>Total Episodes</div>
          </div>
          
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '12px',
            padding: '25px',
            textAlign: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '5px' }}>
              0
            </div>
            <div style={{ fontSize: '14px', opacity: '0.8' }}>30-Day Listens</div>
          </div>
          
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '12px',
            padding: '25px',
            textAlign: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '5px' }}>
              $0.00
            </div>
            <div style={{ fontSize: '14px', opacity: '0.8' }}>30-Day Earnings</div>
          </div>
        </div>

        {/* Add Podcast Actions */}
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Link 
            to="/podcast/import" 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '18px 30px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '10px',
              textDecoration: 'none',
              color: 'white',
              fontWeight: '700',
              fontSize: '16px',
              transition: 'all 0.2s',
              border: '2px solid rgba(255,255,255,0.3)'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.3)';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.2)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span style={{ marginRight: '12px', fontSize: '20px' }}>📤</span>
            Import from RSS
          </Link>
          
          <button 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '18px 30px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '10px',
              color: 'white',
              fontWeight: '700',
              fontSize: '16px',
              transition: 'all 0.2s',
              border: '2px solid rgba(255,255,255,0.3)',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.3)';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.2)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span style={{ marginRight: '12px', fontSize: '20px' }}>✏️</span>
            Create from Scratch
          </button>
        </div>
      </div>
      
      {/* Error Display */}
      {error && (
        <div style={{ 
          padding: '20px',
          background: '#ffeded',
          color: '#e53935',
          borderRadius: '12px',
          marginBottom: '30px',
          borderLeft: '4px solid #e53935',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}
      
      {/* Main Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ 
            width: '60px',
            height: '60px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #4a69dd',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 30px auto'
          }}></div>
          <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Loading your podcasts...</h3>
          <p style={{ margin: 0, color: '#666' }}>This may take a moment</p>
        </div>
      ) : (
        <div>
          {/* Podcasts Section Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px'
          }}>
            <h2 style={{ margin: 0, fontSize: '24px', color: '#333' }}>
              Your Podcasts ({podcasts.length})
            </h2>
            {podcasts.length > 0 && (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button style={{
                  padding: '10px 20px',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  color: '#666',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}>
                  Sort by: Recent
                </button>
                <button style={{
                  padding: '10px 20px',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  color: '#666',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}>
                  Filter
                </button>
              </div>
            )}
          </div>
          
          {podcasts.length > 0 ? (
            /* Podcast Grid */
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
              gap: '30px'
            }}>
              {podcasts.map(podcast => (
                <div key={podcast.id} style={{ 
                  background: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                }}>
                  {/* Podcast Image */}
                  <div style={{ 
                    height: '200px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {podcast.imageURL ? (
                      <img 
                        src={podcast.imageURL} 
                        alt={podcast.title} 
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }} 
                      />
                    ) : (
                      <div style={{ 
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '48px',
                        fontWeight: 'bold'
                      }}>
                        {podcast.title ? podcast.title.charAt(0).toUpperCase() : '🎙️'}
                      </div>
                    )}
                  </div>
                  
                  {/* Podcast Content */}
                  <div style={{ padding: '30px' }}>
                    <h3 style={{ 
                      marginTop: 0, 
                      marginBottom: '12px',
                      fontSize: '20px',
                      lineHeight: '1.3',
                      color: '#2c3e50'
                    }}>
                      {podcast.title}
                    </h3>
                    
                    <p style={{ 
                      color: '#7f8c8d', 
                      fontSize: '14px',
                      margin: '0 0 20px 0',
                      lineHeight: '1.5'
                    }}>
                      {getEpisodeCount(podcast)} episodes • 
                      Last updated {formatLastUpdated(podcast.createdAt || podcast.lastUpdated)}
                    </p>
                    
                    <p style={{ 
                      color: '#95a5a6', 
                      fontSize: '14px',
                      lineHeight: '1.5',
                      margin: '0 0 25px 0',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {podcast.description || 'No description available.'}
                    </p>
                    
                    {/* Analytics Preview */}
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '15px',
                      margin: '25px 0',
                      padding: '20px',
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      borderRadius: '12px'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ 
                          margin: '0 0 5px 0', 
                          fontSize: '11px', 
                          color: '#6c757d',
                          textTransform: 'uppercase',
                          fontWeight: '700',
                          letterSpacing: '0.5px'
                        }}>
                          30-DAY LISTENS
                        </p>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '24px', 
                          fontWeight: '700',
                          color: '#4a69dd'
                        }}>
                          0
                        </p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ 
                          margin: '0 0 5px 0', 
                          fontSize: '11px', 
                          color: '#6c757d',
                          textTransform: 'uppercase',
                          fontWeight: '700',
                          letterSpacing: '0.5px'
                        }}>
                          EARNINGS
                        </p>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '24px', 
                          fontWeight: '700',
                          color: '#27ae60'
                        }}>
                          $0.00
                        </p>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px'
                    }}>
                      <Link 
                        to={`/podcast/${podcast.id}/manage`} 
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '14px',
                          background: '#4a69dd',
                          color: 'white',
                          textAlign: 'center',
                          textDecoration: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '14px',
                          transition: 'all 0.2s',
                          gap: '8px'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#3a5bcb';
                          e.target.style.transform = 'translateY(-1px)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = '#4a69dd';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        <span>⚙️</span>
                        Manage
                      </Link>
                      <Link 
                        to={`/podcast/${podcast.id}/analytics`} 
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '14px',
                          background: '#f8f9fa',
                          color: '#495057',
                          textAlign: 'center',
                          textDecoration: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '14px',
                          border: '1px solid #dee2e6',
                          transition: 'all 0.2s',
                          gap: '8px'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#e9ecef';
                          e.target.style.transform = 'translateY(-1px)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = '#f8f9fa';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        <span>📊</span>
                        Analytics
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Empty State */
            <div style={{ 
              padding: '80px 40px',
              background: 'white',
              borderRadius: '20px',
              textAlign: 'center',
              border: '2px dashed #dee2e6',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
            }}>
              <div style={{ 
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 30px auto',
                fontSize: '48px',
                color: '#6c757d'
              }}>
                🎙️
              </div>
              <h3 style={{ color: '#495057', margin: '0 0 15px 0', fontSize: '24px' }}>
                No podcasts found
              </h3>
              <p style={{ 
                color: '#6c757d', 
                margin: '0 0 40px 0', 
                lineHeight: '1.6',
                fontSize: '16px',
                maxWidth: '500px',
                margin: '0 auto 40px auto'
              }}>
                Start building your podcast empire by importing your first show. 
                You can import from any RSS feed or create from scratch.
              </p>
              <div style={{ 
                display: 'flex', 
                gap: '15px', 
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <Link to="/podcast/import" style={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '18px 30px',
                  background: '#4a69dd',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '16px',
                  gap: '10px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#3a5bcb';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#4a69dd';
                  e.target.style.transform = 'translateY(0)';
                }}
                >
                  <span>📤</span>
                  Import Podcast
                </Link>
                <button style={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '18px 30px',
                  background: 'white',
                  color: '#495057',
                  border: '2px solid #dee2e6',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '16px',
                  cursor: 'pointer',
                  gap: '10px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.transform = 'translateY(0)';
                }}
                >
                  <span>✏️</span>
                  Create from Scratch
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Footer Help Section */}
      {podcasts.length > 0 && (
        <div style={{ 
          marginTop: '60px',
          padding: '30px',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '16px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#495057' }}>
            Need Help Managing Your Podcasts?
          </h3>
          <p style={{ margin: '0 0 20px 0', color: '#6c757d', fontSize: '14px', lineHeight: '1.5' }}>
            Learn how to get the most out of your podcast management tools and grow your audience.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button style={{ 
              padding: '10px 16px',
              background: 'white',
              color: '#495057',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
            >
              📚 Help Center
            </button>
            <button style={{ 
              padding: '10px 16px',
              background: 'white',
              color: '#495057',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
            >
              📹 Video Tutorials
            </button>
            <button style={{ 
              padding: '10px 16px',
              background: 'white',
              color: '#495057',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
            >
              💬 Contact Support
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PodcastManagement;